/**
 * /activar-cuenta — FUNC-124 / SEC-064.
 *
 * El paciente llega desde el correo de invitación (#token=…), elige un PIN de
 * 4 dígitos y su cuenta queda activa. Toda la lógica sensible vive en la Edge
 * Function patient-activate-credential; acá solo se valida formato.
 *
 * El token viaja en el FRAGMENT de la URL (#token=…), no en query string: un
 * fragment nunca sale del navegador hacia el servidor (ni logs de Vercel/
 * hosting, ni Referer, ni "link scanners" de seguridad corporativa que sí
 * hacen GET a los links de un correo). El replaceState de abajo limpia
 * además el historial local, como segunda capa.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { PatientCredentialService } from '../services/patient-credential.service';
import './ActivateAccountPage.css';

type Phase = 'form' | 'submitting' | 'success' | 'invalid_link' | 'support';

const TOKEN_RE = /^[0-9a-f]{64}$/;
const PIN_RE = /^[0-9]{4}$/;

function readTokenFromUrl(): string | null {
  // Formato esperado: #token=<64 hex>. Se acepta también ?token= (compatibilidad con links
  // viejos ya enviados antes de este fix) para no invalidar invitaciones ya despachadas.
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = hashParams.get('token') ?? new URLSearchParams(window.location.search).get('token');
  return token && TOKEN_RE.test(token) ? token : null;
}

const ActivateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [initialToken] = useState(readTokenFromUrl);
  const tokenRef = useRef<string | null>(initialToken);

  const [phase, setPhase] = useState<Phase>(initialToken ? 'form' : 'invalid_link');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [supportMessage, setSupportMessage] = useState<string | null>(null);
  const pinInputRef = useRef<HTMLInputElement>(null);

  // Sacar el token de la URL (y del historial) una vez leído.
  useEffect(() => {
    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (phase === 'form') pinInputRef.current?.focus();
  }, [phase]);

  const onlyDigits = (value: string) => value.replace(/\D/g, '').slice(0, 4);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!PIN_RE.test(pin)) {
      setError('El PIN debe tener exactamente 4 dígitos.');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Los PIN no coinciden.');
      return;
    }
    if (!tokenRef.current) {
      setPhase('invalid_link');
      return;
    }

    setPhase('submitting');
    const result = await PatientCredentialService.activate(tokenRef.current, pin);

    if (result.success) {
      tokenRef.current = null;
      setPin('');
      setPinConfirm('');
      setPhase('success');
      return;
    }

    switch (result.code) {
      case 'weak_pin':
      case 'rate_limited':
        // El token sigue vigente: el paciente puede reintentar con otro PIN / más tarde.
        setError(result.error ?? 'No pudimos activar tu cuenta.');
        setPhase('form');
        break;
      case 'invalid_link':
        setPhase('invalid_link');
        break;
      case 'network':
        setError(result.error ?? 'Problema de conexión. Intentá de nuevo.');
        setPhase('form');
        break;
      default:
        setSupportMessage(result.error ?? null);
        setPhase('support');
    }
  };

  return (
    <div className="activate-account">
      <Helmet>
        <title>Activá tu cuenta — Conectemos Chile</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>

      <main className="activate-account__card">
        <img className="activate-account__logo" src="/assets/img/logo.png" alt="Conectemos Chile" width={56} height={56} />

        {(phase === 'form' || phase === 'submitting') && (
          <>
            <h1 className="activate-account__title">Activá tu cuenta</h1>
            <p className="activate-account__lead">
              Elegí un PIN de 4 dígitos. Lo vas a usar junto con tu RUT para ingresar.
            </p>

            <form className="activate-account__form" onSubmit={handleSubmit} noValidate>
              <label className="activate-account__label" htmlFor="aa-pin">PIN (4 dígitos)</label>
              <input
                id="aa-pin"
                ref={pinInputRef}
                className="activate-account__input"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                pattern="[0-9]{4}"
                maxLength={4}
                value={pin}
                onChange={e => setPin(onlyDigits(e.target.value))}
                disabled={phase === 'submitting'}
                aria-invalid={!!error}
                aria-describedby="aa-pin-hint"
              />

              <label className="activate-account__label" htmlFor="aa-pin-confirm">Repetí tu PIN</label>
              <input
                id="aa-pin-confirm"
                className="activate-account__input"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                pattern="[0-9]{4}"
                maxLength={4}
                value={pinConfirm}
                onChange={e => setPinConfirm(onlyDigits(e.target.value))}
                disabled={phase === 'submitting'}
                aria-invalid={!!error}
              />

              <p id="aa-pin-hint" className="activate-account__hint">
                Evitá secuencias (1234), números repetidos (1111), tu fecha de nacimiento y los primeros 4 dígitos de tu RUT.
              </p>

              {error && <p className="activate-account__error" role="alert">{error}</p>}

              <button className="activate-account__button" type="submit" disabled={phase === 'submitting'}>
                {phase === 'submitting' ? 'Activando…' : 'Activar mi cuenta'}
              </button>
            </form>
          </>
        )}

        {phase === 'success' && (
          <>
            <div className="activate-account__icon" aria-hidden="true">✅</div>
            <h1 className="activate-account__title">¡Cuenta activada!</h1>
            <p className="activate-account__lead">
              Tu PIN quedó guardado. Cuando agendes, ingresá tu RUT y elegí “Ingresá con tu PIN”:
              usamos el email y teléfono que ya tenemos registrados, sin volver a pedírtelos.
            </p>
            <button className="activate-account__button" type="button" onClick={() => navigate('/agendar')}>
              Agendar una hora
            </button>
          </>
        )}

        {phase === 'invalid_link' && (
          <>
            <div className="activate-account__icon" aria-hidden="true">⚠️</div>
            <h1 className="activate-account__title">Link inválido o vencido</h1>
            <p className="activate-account__lead">
              Este link ya se usó o venció (dura 72 horas). Pedile a la clínica que te envíe una invitación nueva:
              escribinos al <a href="https://wa.me/56978009119">+56 9 7800 9119</a>.
            </p>
          </>
        )}

        {phase === 'support' && (
          <>
            <div className="activate-account__icon" aria-hidden="true">⚠️</div>
            <h1 className="activate-account__title">No pudimos terminar la activación</h1>
            <p className="activate-account__lead">
              {supportMessage ?? 'Algo falló de nuestro lado.'} La clínica puede enviarte una invitación nueva.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default ActivateAccountPage;
